/**
 * SegmentCreateManualPage — `/segments/new/manual`
 *
 * Filter builder visual: el usuario arma el segmento agregando reglas tipo
 * (campo, operador, valor). Cada regla es una "row" con dropdowns.
 *
 * Live preview del count: cada vez que cambia una regla, se calcula un
 * estimado (mock determinístico basado en hash del filtro). En producción
 * dispararía una query real.
 *
 * Layout: 2 columnas — izquierda el builder, derecha el preview + save.
 */

import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Plus,
    Trash2,
    Save,
    Edit3,
    Users,
    AlertCircle,
    Loader2,
    Filter as FilterIcon,
    ShoppingBag,
    DollarSign,
    Calendar,
    MapPin,
    User,
    TrendingUp,
    Heart,
    Cake,
    Tag,
    Layers,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { segmentsApi } from "../api";

/* ─── Catálogo de campos filtrables ─── */

type FieldType = "number" | "currency" | "select" | "multiselect" | "month";

interface FieldDef {
    key: string;
    label: string;
    icon: typeof Users;
    type: FieldType;
    /** Para select/multiselect */
    options?: { value: string; label: string }[];
    /** Texto de hint debajo del campo */
    hint?: string;
}

const FIELDS: FieldDef[] = [
    {
        key: "totalOrders",
        label: "Total de pedidos",
        icon: ShoppingBag,
        type: "number",
        hint: "Cuántos pedidos hizo este cliente en total",
    },
    {
        key: "totalSpent",
        label: "Total gastado",
        icon: DollarSign,
        type: "currency",
        hint: "LTV — total acumulado en pedidos",
    },
    {
        key: "avgTicket",
        label: "Ticket promedio",
        icon: TrendingUp,
        type: "currency",
        hint: "Valor promedio por pedido",
    },
    {
        key: "daysSinceLastOrder",
        label: "Días desde último pedido",
        icon: Calendar,
        type: "number",
        hint: "Útil para detectar inactivos",
    },
    {
        key: "daysSinceFirstOrder",
        label: "Días como cliente",
        icon: Heart,
        type: "number",
        hint: "Antigüedad en la base",
    },
    {
        key: "localId",
        label: "Sucursal habitual",
        icon: MapPin,
        type: "select",
        options: [
            { value: "loc-1", label: "Palermo" },
            { value: "loc-2", label: "Belgrano" },
            { value: "loc-3", label: "Recoleta" },
            { value: "loc-4", label: "San Telmo" },
        ],
    },
    {
        key: "tags",
        label: "Etiquetas",
        icon: Tag,
        type: "multiselect",
        options: [
            { value: "vip", label: "VIP" },
            { value: "familia", label: "Familia" },
            { value: "joven", label: "Joven" },
            { value: "corporativo", label: "Corporativo" },
            { value: "delivery", label: "Solo delivery" },
            { value: "salon", label: "Solo salón" },
        ],
    },
    {
        key: "birthMonth",
        label: "Mes de cumpleaños",
        icon: Cake,
        type: "month",
    },
    {
        key: "gender",
        label: "Género",
        icon: User,
        type: "select",
        options: [
            { value: "F", label: "Femenino" },
            { value: "M", label: "Masculino" },
            { value: "O", label: "Otro / No especifica" },
        ],
    },
    {
        key: "age",
        label: "Edad",
        icon: Calendar,
        type: "number",
        hint: "En años",
    },
];

const FIELDS_BY_KEY = FIELDS.reduce<Record<string, FieldDef>>((acc, f) => {
    acc[f.key] = f;
    return acc;
}, {});

const OPERATORS_BY_TYPE: Record<FieldType, { value: string; label: string }[]> = {
    number: [
        { value: "eq", label: "Igual a" },
        { value: "neq", label: "Distinto de" },
        { value: "lt", label: "Menor que" },
        { value: "lte", label: "Menor o igual" },
        { value: "gt", label: "Mayor que" },
        { value: "gte", label: "Mayor o igual" },
    ],
    currency: [
        { value: "lt", label: "Menor a" },
        { value: "lte", label: "Menor o igual a" },
        { value: "gt", label: "Mayor a" },
        { value: "gte", label: "Mayor o igual a" },
    ],
    select: [
        { value: "eq", label: "Es" },
        { value: "neq", label: "No es" },
    ],
    multiselect: [
        { value: "in", label: "Incluye alguna de" },
        { value: "all", label: "Incluye todas" },
        { value: "nin", label: "No incluye" },
    ],
    month: [
        { value: "eq", label: "Es" },
        { value: "neq", label: "No es" },
    ],
};

const MONTHS = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

/* ─── Modelo de regla ─── */

interface Rule {
    id: string;
    fieldKey: string;
    operator: string;
    value: any;
}

/* ─── Estimación mock del count ─── */
/**
 * Calcula un count plausible basado en las reglas. Cada regla "filtra" un
 * porcentaje del total. Combinación = multiplicación de fracciones.
 *
 * Esto es un mock — en producción es una query agregada en backend.
 */
function estimateCount(rules: Rule[], baseUserCount: number = 12480): number {
    if (rules.length === 0) return baseUserCount;
    let fraction = 1;
    for (const r of rules) {
        if (!r.fieldKey || !r.operator || r.value === undefined || r.value === "") {
            // Regla incompleta: la ignoramos
            continue;
        }
        // Cada field aporta una fracción distinta — basado en heurísticas
        // restaurant-realistas
        switch (r.fieldKey) {
            case "totalOrders":
                fraction *= r.operator === "gte" && Number(r.value) >= 5 ? 0.18 : 0.45;
                break;
            case "totalSpent":
                fraction *= r.operator === "gte" && Number(r.value) >= 30000 ? 0.12 : 0.5;
                break;
            case "avgTicket":
                fraction *= r.operator === "lt" ? 0.55 : 0.4;
                break;
            case "daysSinceLastOrder":
                fraction *= r.operator === "gte" && Number(r.value) >= 30 ? 0.35 : 0.65;
                break;
            case "daysSinceFirstOrder":
                fraction *= 0.7;
                break;
            case "localId":
                fraction *= 0.27; // ~1/4 de los clientes por sucursal
                break;
            case "tags":
                fraction *= 0.3;
                break;
            case "birthMonth":
                fraction *= 0.083; // ~1/12
                break;
            case "gender":
                fraction *= 0.5;
                break;
            case "age":
                fraction *= 0.3;
                break;
            default:
                fraction *= 0.5;
        }
    }
    return Math.max(1, Math.round(baseUserCount * fraction));
}

export function SegmentCreateManualPage() {
    const navigate = useNavigate();

    const [rules, setRules] = useState<Rule[]>([
        // Una regla vacía inicial para que el user vea de qué se trata
        { id: "r-1", fieldKey: "", operator: "", value: "" },
    ]);
    const [segmentName, setSegmentName] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    /** Una regla es válida si tiene campo + operador + valor non-empty.
     *  Para multiselect el value es array — chequeamos length > 0. */
    const validRules = rules.filter((r) => {
        if (!r.fieldKey || !r.operator) return false;
        if (Array.isArray(r.value)) return r.value.length > 0;
        return r.value !== "" && r.value !== undefined && r.value !== null;
    });
    const count = useMemo(() => estimateCount(validRules), [validRules]);
    const canSave = validRules.length > 0 && segmentName.trim().length > 0;

    const addRule = () => {
        setRules((prev) => [
            ...prev,
            { id: `r-${Date.now()}`, fieldKey: "", operator: "", value: "" },
        ]);
    };

    const removeRule = (id: string) => {
        setRules((prev) => prev.filter((r) => r.id !== id));
    };

    const updateRule = (id: string, patch: Partial<Rule>) => {
        setRules((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                const next = { ...r, ...patch };
                // Si cambia el campo, reseteamos operator y value
                if (patch.fieldKey !== undefined && patch.fieldKey !== r.fieldKey) {
                    next.operator = "";
                    next.value = "";
                }
                return next;
            }),
        );
    };

    /** Traduce las reglas a un filter shape estilo Mongo y a un summary legible.
     *  El handler del backend lo persiste tal cual — el dashboard del segmento
     *  después usa filteredFields para mostrar los chips. */
    const buildFilterAndSummary = () => {
        const filter: Record<string, unknown> = {};
        const filteredFields: string[] = [];
        const summaryParts: string[] = [];

        for (const r of validRules) {
            const fd = FIELDS_BY_KEY[r.fieldKey];
            if (!fd) continue;
            filteredFields.push(r.fieldKey);

            // Mongo-style filter shape
            switch (r.operator) {
                case "eq":
                    filter[r.fieldKey] = r.value;
                    break;
                case "neq":
                    filter[r.fieldKey] = { $ne: r.value };
                    break;
                case "lt":
                    filter[r.fieldKey] = { $lt: r.value };
                    break;
                case "lte":
                    filter[r.fieldKey] = { $lte: r.value };
                    break;
                case "gt":
                    filter[r.fieldKey] = { $gt: r.value };
                    break;
                case "gte":
                    filter[r.fieldKey] = { $gte: r.value };
                    break;
                case "in":
                    filter[r.fieldKey] = { $in: r.value };
                    break;
                case "all":
                    filter[r.fieldKey] = { $all: r.value };
                    break;
                case "nin":
                    filter[r.fieldKey] = { $nin: r.value };
                    break;
                default:
                    filter[r.fieldKey] = r.value;
            }

            // Summary legible
            const op = OPERATORS_BY_TYPE[fd.type].find(
                (o) => o.value === r.operator,
            );
            let valueStr = String(r.value);
            if (fd.type === "select" && fd.options) {
                valueStr =
                    fd.options.find((o) => o.value === r.value)?.label ||
                    valueStr;
            } else if (fd.type === "multiselect" && fd.options) {
                const arr = Array.isArray(r.value) ? r.value : [];
                valueStr = arr
                    .map(
                        (v) =>
                            fd.options!.find((o) => o.value === v)?.label || v,
                    )
                    .join(", ");
            } else if (fd.type === "currency") {
                valueStr = `$${Number(r.value).toLocaleString("es-AR")}`;
            } else if (fd.type === "month") {
                valueStr = MONTHS[Number(r.value) - 1] || valueStr;
            }
            summaryParts.push(
                `${fd.label} ${op?.label.toLowerCase() || ""} ${valueStr}`,
            );
        }

        return {
            filter,
            filteredFields,
            description: summaryParts.join(" · "),
        };
    };

    const handleSave = async () => {
        if (!canSave) return;
        setSaving(true);
        setError(null);
        const { filter, filteredFields, description } = buildFilterAndSummary();
        const res = await segmentsApi.createMock({
            brandId: "brand-demo",
            name: segmentName.trim(),
            description,
            filter,
            filteredFields,
            lastKnownCount: count,
            emoji: "🎯",
        });
        setSaving(false);
        if (res.success && res.data) {
            navigate(`/segments/${res.data.id}`);
        } else {
            setError(res.error || "Error al guardar segmento");
        }
    };

    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-6xl space-y-6">
                {/* Breadcrumb */}
                <Link
                    to="/segments/new"
                    className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
                >
                    <ArrowLeft size={14} />
                    Cambiar de método
                </Link>

                {/* Header */}
                <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
                    <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1 mb-2">
                            <Edit3 size={9} />
                            Manual
                        </span>
                        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                            Armar segmento con filtros
                        </h1>
                        <p className="text-slate-500 text-sm mt-1">
                            Agregá reglas usando campo + operador + valor. Cada regla
                            es un AND con las anteriores.
                        </p>
                    </div>
                </div>

                {/* Layout 2 columnas */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left 2/3: filter builder */}
                    <div className="lg:col-span-2 space-y-3">
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <FilterIcon size={16} className="text-emerald-600" />
                                    <h2 className="font-bold text-slate-900">
                                        Reglas del segmento
                                    </h2>
                                    {validRules.length > 0 && (
                                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                            {validRules.length} activa
                                            {validRules.length > 1 ? "s" : ""}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Reglas */}
                            <div className="space-y-3">
                                {rules.map((rule, idx) => (
                                    <RuleRow
                                        key={rule.id}
                                        rule={rule}
                                        index={idx}
                                        onChange={(patch) => updateRule(rule.id, patch)}
                                        onRemove={() => removeRule(rule.id)}
                                        canRemove={rules.length > 1}
                                    />
                                ))}
                            </div>

                            {/* Add rule button */}
                            <button
                                onClick={addRule}
                                className="mt-4 w-full py-2.5 rounded-xl border-2 border-dashed border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 text-sm font-medium text-slate-600 hover:text-emerald-700 transition-all flex items-center justify-center gap-2"
                            >
                                <Plus size={15} />
                                Agregar otra regla
                            </button>
                        </div>

                        {/* Catálogo helper — solo si no hay reglas válidas */}
                        {validRules.length === 0 && (
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                                    💡 Ejemplos rápidos
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <ExampleChip
                                        label="VIP (5+ pedidos)"
                                        onClick={() =>
                                            setRules([
                                                {
                                                    id: "r-1",
                                                    fieldKey: "totalOrders",
                                                    operator: "gte",
                                                    value: 5,
                                                },
                                            ])
                                        }
                                    />
                                    <ExampleChip
                                        label="Inactivos 30d+"
                                        onClick={() =>
                                            setRules([
                                                {
                                                    id: "r-1",
                                                    fieldKey: "daysSinceLastOrder",
                                                    operator: "gte",
                                                    value: 30,
                                                },
                                            ])
                                        }
                                    />
                                    <ExampleChip
                                        label="Ticket bajo (<$3k)"
                                        onClick={() =>
                                            setRules([
                                                {
                                                    id: "r-1",
                                                    fieldKey: "avgTicket",
                                                    operator: "lt",
                                                    value: 3000,
                                                },
                                            ])
                                        }
                                    />
                                    <ExampleChip
                                        label="Solo Palermo"
                                        onClick={() =>
                                            setRules([
                                                {
                                                    id: "r-1",
                                                    fieldKey: "localId",
                                                    operator: "eq",
                                                    value: "loc-1",
                                                },
                                            ])
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right 1/3: live preview + save */}
                    <div className="space-y-4">
                        {/* Live count */}
                        <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-2xl border-2 border-violet-200 p-5 sticky top-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Users size={14} className="text-violet-700" />
                                <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700">
                                    Estimación
                                </p>
                            </div>
                            <p className="text-5xl font-black text-violet-700 tabular-nums leading-none">
                                {count.toLocaleString("es-AR")}
                            </p>
                            <p className="text-sm text-violet-700/80 mt-2">
                                usuarios coinciden
                            </p>
                            <p className="text-[10px] text-slate-500 mt-3 leading-snug">
                                {validRules.length === 0
                                    ? "Sin filtros — incluye toda la base de 12.480 clientes."
                                    : `${validRules.length} regla${
                                          validRules.length > 1 ? "s" : ""
                                      } aplicada${validRules.length > 1 ? "s" : ""} sobre tu base activa.`}
                            </p>
                        </div>

                        {/* Save form */}
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-900 mb-1.5">
                                    Nombre del segmento
                                </label>
                                <input
                                    type="text"
                                    value={segmentName}
                                    onChange={(e) =>
                                        setSegmentName(e.target.value.slice(0, 80))
                                    }
                                    placeholder="Ej. VIP Belgrano · 30d+"
                                    maxLength={80}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all"
                                />
                                <p className="text-[10px] text-slate-400 mt-1 text-right font-mono">
                                    {segmentName.length}/80
                                </p>
                            </div>

                            {error && (
                                <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5 flex items-start gap-1.5">
                                    <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
                                    {error}
                                </div>
                            )}

                            <Button
                                variant="primary"
                                onClick={handleSave}
                                disabled={!canSave || saving}
                                className="w-full"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 size={14} className="animate-spin" />
                                        Guardando…
                                    </>
                                ) : (
                                    <>
                                        <Save size={14} />
                                        Guardar segmento
                                    </>
                                )}
                            </Button>

                            {!canSave && (
                                <p className="text-[10px] text-slate-400 leading-snug">
                                    {validRules.length === 0
                                        ? "Agregá al menos una regla completa"
                                        : "Ponele un nombre antes de guardar"}
                                </p>
                            )}
                        </div>

                        {/* Switch a IA tip */}
                        <Link
                            to="/segments/new/ai"
                            className="block bg-slate-50 border border-slate-200 rounded-xl p-3 hover:bg-violet-50 hover:border-violet-200 transition-colors group"
                        >
                            <div className="flex items-start gap-2.5">
                                <Layers
                                    size={14}
                                    className="text-violet-600 mt-0.5 flex-shrink-0"
                                />
                                <div>
                                    <p className="text-xs font-semibold text-slate-900 group-hover:text-violet-700">
                                        ¿Demasiados dropdowns?
                                    </p>
                                    <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                                        Cambiar a modo IA y describir el segmento en
                                        una frase →
                                    </p>
                                </div>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ─── Sub-component: una row del builder ─── */
function RuleRow({
    rule,
    index,
    onChange,
    onRemove,
    canRemove,
}: {
    rule: Rule;
    index: number;
    onChange: (patch: Partial<Rule>) => void;
    onRemove: () => void;
    canRemove: boolean;
}) {
    const fieldDef = rule.fieldKey ? FIELDS_BY_KEY[rule.fieldKey] : null;
    const operators = fieldDef ? OPERATORS_BY_TYPE[fieldDef.type] : [];

    return (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-3">
            <div className="flex items-center gap-2 mb-2">
                {index === 0 ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        Donde
                    </span>
                ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
                        Y
                    </span>
                )}
                <span className="text-[10px] text-slate-400">Regla {index + 1}</span>
                {canRemove && (
                    <button
                        onClick={onRemove}
                        className="ml-auto p-1 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                        aria-label="Quitar regla"
                    >
                        <Trash2 size={13} />
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {/* 1. Field */}
                <select
                    value={rule.fieldKey}
                    onChange={(e) => onChange({ fieldKey: e.target.value })}
                    aria-label="Campo"
                    className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-emerald-300 cursor-pointer"
                >
                    <option value="">Elegí un campo…</option>
                    {FIELDS.map((f) => (
                        <option key={f.key} value={f.key}>
                            {f.label}
                        </option>
                    ))}
                </select>

                {/* 2. Operator */}
                <select
                    value={rule.operator}
                    onChange={(e) => onChange({ operator: e.target.value })}
                    disabled={!fieldDef}
                    aria-label="Operador"
                    className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-emerald-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <option value="">Operador…</option>
                    {operators.map((op) => (
                        <option key={op.value} value={op.value}>
                            {op.label}
                        </option>
                    ))}
                </select>

                {/* 3. Value */}
                <ValueInput
                    field={fieldDef}
                    value={rule.value}
                    onChange={(v) => onChange({ value: v })}
                />
            </div>

            {/* Hint del field */}
            {fieldDef?.hint && (
                <p className="text-[10px] text-slate-400 mt-2 ml-1">
                    💡 {fieldDef.hint}
                </p>
            )}
        </div>
    );
}

/* ─── Sub-component: value input según tipo ─── */
function ValueInput({
    field,
    value,
    onChange,
}: {
    field: FieldDef | null;
    value: any;
    onChange: (v: any) => void;
}) {
    if (!field) {
        return (
            <input
                disabled
                aria-label="Valor"
                placeholder="Valor…"
                className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm opacity-50"
            />
        );
    }

    if (field.type === "select" && field.options) {
        return (
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                aria-label="Valor"
                className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-emerald-300 cursor-pointer"
            >
                <option value="">Valor…</option>
                {field.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
        );
    }

    if (field.type === "multiselect" && field.options) {
        const selected: string[] = Array.isArray(value) ? value : [];
        return (
            <div className="px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs min-h-[40px] flex flex-wrap gap-1 items-center">
                {field.options.map((opt) => {
                    const isOn = selected.includes(opt.value);
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                                const next = isOn
                                    ? selected.filter((v) => v !== opt.value)
                                    : [...selected, opt.value];
                                onChange(next);
                            }}
                            className={`px-2 py-0.5 rounded-md text-[10px] font-semibold transition-colors ${
                                isOn
                                    ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>
        );
    }

    if (field.type === "month") {
        return (
            <select
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                aria-label="Mes"
                className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-emerald-300 cursor-pointer"
            >
                <option value="">Mes…</option>
                {MONTHS.map((m, i) => (
                    <option key={m} value={i + 1}>
                        {m}
                    </option>
                ))}
            </select>
        );
    }

    // number o currency
    return (
        <div className="relative">
            {field.type === "currency" && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                    $
                </span>
            )}
            <input
                type="number"
                value={value ?? ""}
                onChange={(e) =>
                    onChange(e.target.value === "" ? "" : Number(e.target.value))
                }
                placeholder={field.type === "currency" ? "0" : "Valor"}
                className={`w-full py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 font-mono focus:outline-none focus:border-emerald-300 ${
                    field.type === "currency" ? "pl-7 pr-3" : "px-3"
                }`}
            />
        </div>
    );
}

function ExampleChip({
    label,
    onClick,
}: {
    label: string;
    onClick: () => void;
}) {
    return (
        <button
            onClick={onClick}
            className="px-2.5 py-1.5 text-[11px] font-semibold rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-colors"
        >
            {label}
        </button>
    );
}

export default SegmentCreateManualPage;
